/* Root & Iron storefront behaviour: add to cart, product purchase options, bundles, gallery, menu, cart count and the email popup. */
(() => {
  'use strict';

  const CART_EVENT = 'shopify:cart:lines-update';
  const routes = () => (window.Theme && window.Theme.routes) || {};
  const store = {
    get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ } },
  };

  function money(cents, currency) {
    const amount = Number(cents) / 100;
    const whole = Number.isInteger(amount);
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(amount);
    } catch (e) {
      return '$' + (whole ? amount.toFixed(0) : amount.toFixed(2));
    }
  }

  function toast(message) {
    let el = document.querySelector('.ri-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'ri-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    window.clearTimeout(el.riTimer);
    el.riTimer = window.setTimeout(() => el.classList.remove('is-visible'), 4000);
  }

  function openCartDrawer(actions) {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && actions && typeof actions.openCart === 'function') {
      if (!drawer.isOpen) actions.openCart();
      return;
    }
    window.location.href = routes().cart_url || '/cart';
  }

  async function addLines(lines) {
    const actions = window.Shopify && window.Shopify.actions;
    if (actions && typeof actions.updateCart === 'function') {
      const result = await actions.updateCart({
        lines: lines.map((line) => {
          const item = { merchandiseId: String(line.id), quantity: Number(line.quantity) || 1 };
          if (line.selling_plan) item.sellingPlanId = String(line.selling_plan);
          return item;
        }),
      }, { event: { context: 'product' } });
      if (result && result.userErrors && result.userErrors.length) throw new Error(result.userErrors[0].message);
      window.setTimeout(() => openCartDrawer(actions), 300);
      return result;
    }
    const response = await fetch(routes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => {
          const item = { id: Number(line.id), quantity: Number(line.quantity) || 1 };
          if (line.selling_plan) item.selling_plan = Number(line.selling_plan);
          return item;
        }),
      }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    window.location.href = routes().cart_url || '/cart';
    return null;
  }

  async function withBusy(button, errorEl, task) {
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    try {
      await task();
    } catch (error) {
      const message = (error && error.message) || 'Something went wrong. Please try again.';
      if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; } else { toast(message); }
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  function updateCount(count) {
    document.querySelectorAll('[data-ri-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle('is-empty', count === 0);
    });
    document.querySelectorAll('[data-ri-open-cart]').forEach((el) => {
      el.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items'));
    });
  }

  document.addEventListener(CART_EVENT, (event) => {
    if (!event.promise) return;
    event.promise.then((payload) => {
      const cart = payload && payload.cart;
      const detail = payload && payload.detail;
      const count = cart && typeof cart.totalQuantity === 'number' ? cart.totalQuantity : detail && detail.itemCount;
      if (typeof count === 'number') updateCount(count);
    }).catch(() => {});
  });

  function initBuy(form) {
    if (form.dataset.riReady === 'true') return;
    const dataEl = form.querySelector('[data-ri-buy-data]');
    if (!dataEl) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    form.dataset.riReady = 'true';

    const qtyInput = form.querySelector('[data-ri-qty-input]');
    const planInput = form.querySelector('[data-ri-plan-input]');
    const radios = form.querySelectorAll('[data-ri-plan-radio]');
    const qtyButtons = form.querySelectorAll('[data-ri-qty]');
    const subCard = form.querySelector('[data-ri-plan-card="sub"]');
    const subPrice = form.querySelector('[data-ri-sub-price]');
    const subNote = form.querySelector('[data-ri-sub-note]');
    const subBadge = subCard ? subCard.querySelector('.ri-plan__badge') : null;
    const oncePrices = form.querySelectorAll('[data-ri-once-price]');
    const cta = form.querySelector('[data-ri-cta]');
    const error = form.querySelector('[data-ri-error]');
    const plans = data.plans || {};
    const breaks = data.breaks || {};
    const state = { qty: 1, mode: plans['1'] ? 'sub' : 'once' };

    const breakFor = (qty) => {
      if (!data.breaksLive) return 0;
      if (qty >= 6) return (Number(breaks['6']) || 0) / 100;
      if (qty >= 3) return (Number(breaks['3']) || 0) / 100;
      return 0;
    };

    function render() {
      const plan = plans[String(state.qty)] || null;
      if (!plan && state.mode === 'sub') state.mode = 'once';
      radios.forEach((radio) => {
        if (radio.value === 'sub') radio.disabled = !plan;
        radio.checked = radio.value === state.mode;
      });
      if (subCard) subCard.classList.toggle('is-disabled', !plan);
      if (subPrice) subPrice.textContent = plan ? money(plan.price * state.qty, data.currency) : '—';
      if (subNote) subNote.textContent = plan ? (state.qty === 1 ? 'per delivery' : 'per ' + state.qty + '-bottle delivery') : 'Not available';
      const once = Math.round(data.price * state.qty * (1 - breakFor(state.qty)));
      oncePrices.forEach((el) => { el.textContent = money(once, data.currency); });
      if (subBadge) subBadge.style.display = plan && plan.price * state.qty < once ? '' : 'none';
      qtyButtons.forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.riQty) === state.qty)));
      if (qtyInput) qtyInput.value = String(state.qty);
      if (planInput) {
        const usePlan = state.mode === 'sub' && plan;
        planInput.value = usePlan ? String(plan.id) : '';
        planInput.disabled = !usePlan;
      }
      if (cta) cta.textContent = !data.available ? data.labels.soldOut : (state.mode === 'sub' ? data.labels.ctaSub : data.labels.ctaOnce);
    }

    radios.forEach((radio) => radio.addEventListener('change', () => {
      if (radio.checked) { state.mode = radio.value; render(); }
    }));
    qtyButtons.forEach((button) => button.addEventListener('click', () => {
      state.qty = Number(button.dataset.riQty) || 1;
      render();
    }));
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!data.available || !data.variantId) return;
      const plan = state.mode === 'sub' ? plans[String(state.qty)] : null;
      withBusy(cta, error, () => addLines([{ id: data.variantId, quantity: state.qty, selling_plan: plan ? plan.id : null }]));
    });
    form.riSelectQty = (qty) => { state.qty = qty; render(); };
    render();
  }

  function showInGallery(thumb) {
    const gallery = thumb.closest('[data-ri-gallery]');
    if (!gallery) return;
    const img = gallery.querySelector('.ri-product__main-img');
    const bottle = gallery.querySelector('.ri-product__bottle-wrap');
    if (thumb.hasAttribute('data-bottle')) {
      if (!bottle) return;
      bottle.hidden = false;
      if (img) img.hidden = true;
    } else {
      if (!img || !thumb.dataset.src) return;
      img.src = thumb.dataset.src;
      img.srcset = thumb.dataset.srcset || '';
      img.alt = thumb.dataset.alt || '';
      img.classList.toggle('is-photo', thumb.hasAttribute('data-photo'));
      img.hidden = false;
      if (bottle) bottle.hidden = true;
    }
    gallery.querySelectorAll('[data-ri-thumb]').forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const add = target.closest('[data-ri-add]');
    if (add) {
      event.preventDefault();
      withBusy(add, null, () => addLines([{ id: add.dataset.variantId, quantity: add.dataset.quantity || 1 }]));
      return;
    }

    const bundle = target.closest('[data-ri-bundle]');
    if (bundle) {
      event.preventDefault();
      let lines = [];
      try { lines = JSON.parse(bundle.dataset.lines || '[]'); } catch (e) { lines = []; }
      if (lines.length) withBusy(bundle, null, () => addLines(lines));
      return;
    }

    const cartLink = target.closest('[data-ri-open-cart]');
    if (cartLink) {
      const actions = window.Shopify && window.Shopify.actions;
      if (document.querySelector('theme-drawer#cart-drawer') && actions && typeof actions.openCart === 'function') {
        event.preventDefault();
        actions.openCart();
      }
      return;
    }

    const pick = target.closest('[data-ri-select-qty]');
    if (pick) {
      const form = document.querySelector('[data-ri-buy]');
      if (form && typeof form.riSelectQty === 'function') {
        event.preventDefault();
        form.riSelectQty(Number(pick.dataset.riSelectQty) || 3);
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const thumb = target.closest('[data-ri-thumb]');
    if (thumb) {
      showInGallery(thumb);
      return;
    }

    const menuOpen = target.closest('[data-ri-menu-open]');
    if (menuOpen) {
      const dialog = document.getElementById(menuOpen.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
        dialog.showModal();
        menuOpen.setAttribute('aria-expanded', 'true');
        dialog.addEventListener('close', () => menuOpen.setAttribute('aria-expanded', 'false'), { once: true });
      }
      return;
    }

    const menuClose = target.closest('[data-ri-menu-close], [data-ri-menu-link]');
    if (menuClose) {
      const dialog = menuClose.closest('dialog');
      if (dialog && dialog.open) dialog.close();
      return;
    }

    if (target.matches('dialog.ri-menu')) target.close();
  });

  /* The page scrolls inside .page-wrapper on desktop and in the document on mobile. */
  function scrollProgress() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && window.getComputedStyle(wrapper).overflowY !== 'visible') {
      const max = wrapper.scrollHeight - wrapper.clientHeight;
      if (max > 0 && wrapper.scrollTop > 0) return wrapper.scrollTop / max;
    }
    const docMax = document.documentElement.scrollHeight - window.innerHeight;
    return docMax > 0 ? window.scrollY / docMax : 0;
  }

  class RiPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.dataset.riReady === 'true') return;
      this.dataset.riReady = 'true';
      this.dialog.addEventListener('close', () => this.remember());
      this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
      this.querySelectorAll('[data-ri-popup-close]').forEach((button) => button.addEventListener('click', () => this.dialog.close()));

      if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') store.set('ri-popup-joined', '1');

      if (this.hasAttribute('data-design-mode')) {
        document.addEventListener('shopify:section:select', (event) => { if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.open(true); });
        document.addEventListener('shopify:section:deselect', (event) => { if (event.detail && event.detail.sectionId === this.dataset.sectionId && this.dialog.open) this.dialog.close(); });
        return;
      }
      if (window.location.hash === '#' + this.dataset.formId && this.querySelector('[data-ri-popup-result]')) { this.open(true); return; }
      if (store.get('ri-popup-joined') === '1') return;
      if (Number(store.get('ri-popup-until') || 0) > Date.now()) return;
      this.arm();
    }

    arm() {
      const delay = Math.max(0, Number(this.dataset.delay || 10)) * 1000;
      const threshold = Math.min(100, Math.max(1, Number(this.dataset.scroll || 50))) / 100;
      let timer = 0;
      const onScroll = () => { if (scrollProgress() >= threshold) trigger(); };
      const cleanup = () => { window.clearTimeout(timer); document.removeEventListener('scroll', onScroll, true); };
      const trigger = () => { cleanup(); this.open(false); };
      timer = window.setTimeout(trigger, delay);
      document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    }

    open(force) {
      if (this.dialog.open) return;
      let busy = false;
      try { busy = Boolean(document.querySelector('dialog:modal')); } catch (e) { busy = false; }
      const drawerOpen = document.querySelector('.page-wrapper--drawer-open');
      if (!force && (busy || drawerOpen)) {
        window.setTimeout(() => this.open(false), 8000);
        return;
      }
      this.dialog.showModal();
      if (!this.hasAttribute('data-design-mode')) this.remember();
    }

    remember() {
      if (this.hasAttribute('data-design-mode') || store.get('ri-popup-joined') === '1') return;
      const days = Math.max(1, Number(this.dataset.days || 30));
      store.set('ri-popup-until', String(Date.now() + days * 86400000));
    }
  }
  if (!customElements.get('ri-popup')) customElements.define('ri-popup', RiPopup);

  function init(root) {
    (root || document).querySelectorAll('[data-ri-buy]').forEach(initBuy);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
